'use client';

/**
 * Изменяющие запросы.
 *
 * Каждая мутация сама сбрасывает то, что она сломала: расхождение между
 * тем, что изменили, и тем, что перечитали, не ломает сборку — оно просто
 * оставляет на экране устаревшие данные, и заметно это только глазами.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ApiError } from './client';
import { queryKeys } from './keys';
import {
  addCallTag,
  addTenantAdmin,
  applyCallFieldTemplate,
  applyTagTemplate,
  archiveUnit,
  assignExtensionUnit,
  assignQueueUnit,
  closeAssignment,
  createAdminTenant,
  createAssignment,
  createBinding,
  createCallField,
  createComment,
  createDisclosureGrant,
  createEmployee,
  createIntegration,
  acceptStationRoute,
  createCallGoal,
  createCtSite,
  createMissedAction,
  createNotificationRule,
  createPool,
  createStaticRule,
  createRule,
  createScorecard,
  createScorecardFromTemplate,
  createServer,
  createSource,
  createStorageProfile,
  createTag,
  createTagRule,
  createUnit,
  createVocabularyItem,
  deactivateEmployee,
  decideDepartmentSuggestion,
  deleteBinding,
  deleteAdName,
  deleteCallField,
  deleteCallGoal,
  deleteComment,
  deleteIntegration,
  deleteMissedAction,
  deleteNotificationRule,
  deleteRule,
  deleteScorecard,
  deleteStaticRule,
  deleteUnitAddendum,
  dryRunCallGoal,
  deleteStorageProfile,
  deleteTag,
  deleteTagRule,
  deleteVocabularyItem,
  hidePendingOrganization,
  markAllNotificationsRead,
  markNotificationRead,
  removeCallTag,
  removeTenantAdmin,
  renameScorecard,
  replaceUserGrants,
  reviewScoreResult,
  revokeDisclosureGrant,
  rotateServerSecret,
  rotateSourceSecret,
  putAdNames,
  putIntegrationMappings,
  setCallRating,
  setIntegrationSecrets,
  setUnitAddendum,
  setNotificationContacts,
  setNotificationSubscription,
  setServerRecordingStereo,
  setServerStorage,
  setSourceStorage,
  setTenantRetention,
  setUnitScorecard,
  testNotificationRule,
  testRule,
  testSourceConnection,
  testStorageProfile,
  testTagRule,
  testWebhookDraft,
  updateAnalysisSettings,
  updateCallField,
  updateContact,
  updateDestinationRouting,
  updateEmployee,
  updateNotificationRule,
  updateRule,
  updateSource,
  updateStorageProfile,
  updateTag,
  updateTagRule,
  updateUnit,
  enrollNumbers,
  testIntegration,
  testIntegrationLookup,
  updateCallGoal,
  updateCtNumber,
  updateCtSite,
  updateIntegration,
  updatePool,
  updateStaticRule,
  updateVocabularyItem,
} from './endpoints';
import type {
  AdminTenantInput,
  AnalysisSettingsInput,
  AssignmentInput,
  BindingInput,
  CallFieldInput,
  ContactInput,
  DepartmentSuggestionStatus,
  DestinationRoutingInput,
  DisclosureGrantInput,
  EmployeeInput,
  GrantInput,
  AdNameInput,
  CallGoalDryRunInput,
  CallGoalInput,
  CtEnrollRequest,
  CtNumberPatch,
  CtPoolInput,
  CtSiteInput,
  CtStaticRuleInput,
  UnitPromptAddendumInput,
  IntegrationInput,
  IntegrationMappingsInput,
  IntegrationSecretsInput,
  MissedActionInput,
  NotificationContactsInput,
  NotificationsPage,
  NotificationRuleInput,
  PbxServerInput,
  ReviewResult,
  RuleInput,
  RuleTestRequest,
  ScorecardDirection,
  SourceInput,
  StorageProfileInput,
  TagInput,
  TagRuleConditions,
  TagRuleInput,
  UnitInput,
  VocabularyInput,
  WebhookDraftTest,
} from './types';

export function useCreateUnit() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: UnitInput) => createUnit(body),
    onSuccess: () => client.invalidateQueries({ queryKey: queryKeys.units }),
  });
}

export function useUpdateUnit() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UnitInput }) => updateUnit(id, body),
    onSuccess: (_data, variables) => {
      void client.invalidateQueries({ queryKey: queryKeys.units });
      // Чек-лист узла мог смениться, а он наследуется вниз по дереву.
      void client.invalidateQueries({ queryKey: ['units'] });
      void client.invalidateQueries({ queryKey: ['units', variables.id, 'scorecard'] });
    },
  });
}

/**
 * Архивация.
 *
 * Узел остаётся в дереве со статусом `archived` — интерфейс не должен ждать,
 * что запись исчезнет: на неё ссылаются исторические звонки и счета.
 * Заодно сбрасываем список звонков: там показывается имя узла.
 */
export function useArchiveUnit() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => archiveUnit(id),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryKeys.units });
      void client.invalidateQueries({ queryKey: ['calls'] });
    },
  });
}

// ───────────────────── Источники и маршруты ─────────────────────────────

export function useCreateSource() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ body, tenant }: { body: SourceInput; tenant?: string }) =>
      createSource(body, tenant),
    onSuccess: () => client.invalidateQueries({ queryKey: queryKeys.sources }),
  });
}

export function useUpdateSource() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body, tenant }: { id: string; body: SourceInput; tenant?: string }) =>
      updateSource(id, body, tenant),
    onSuccess: () => client.invalidateQueries({ queryKey: queryKeys.sources }),
  });
}

/**
 * Смена ключа приёма.
 *
 * Прежний ключ перестаёт работать немедленно, а новый показывается один раз:
 * в базе лежит только хэш. Список источников после этого перечитываем ради
 * даты ротации.
 */
export function useRotateSecret() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, tenant }: { id: string; tenant?: string }) => rotateSourceSecret(id, tenant),
    onSuccess: () => client.invalidateQueries({ queryKey: queryKeys.sources }),
  });
}

/** Диагностика нашей стороны приёма. Ничего не меняет — кэш не трогаем. */
export function useTestConnection() {
  return useMutation({
    mutationFn: ({ id, tenant }: { id: string; tenant?: string }) =>
      testSourceConnection(id, tenant),
  });
}

export function useCreateBinding() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({
      sourceId,
      body,
      tenant,
    }: {
      sourceId: string;
      body: BindingInput;
      tenant?: string;
    }) => createBinding(sourceId, body, tenant),
    onSuccess: (_data, variables) => {
      void client.invalidateQueries({ queryKey: queryKeys.bindings(variables.sourceId) });
      // bindingsCount живёт в строке источника.
      void client.invalidateQueries({ queryKey: queryKeys.sources });
    },
  });
}

// ────────────────────────── Правила чек-листов ──────────────────────────

/**
 * После правки правила сбрасываем и список чек-листов: в нём лежит счётчик
 * включённых правил, и выключенное правило иначе продолжает там числиться.
 */
function invalidateRules(client: ReturnType<typeof useQueryClient>, scorecardId: string) {
  void client.invalidateQueries({ queryKey: queryKeys.rules(scorecardId) });
  void client.invalidateQueries({ queryKey: queryKeys.scorecards });
}

export function useCreateRule() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ scorecardId, body }: { scorecardId: string; body: RuleInput }) =>
      createRule(scorecardId, body),
    onSuccess: (_data, variables) => invalidateRules(client, variables.scorecardId),
  });
}

export function useUpdateRule() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({
      scorecardId,
      ruleId,
      body,
    }: {
      scorecardId: string;
      ruleId: string;
      body: RuleInput;
    }) => updateRule(scorecardId, ruleId, body),
    onSuccess: (_data, variables) => invalidateRules(client, variables.scorecardId),
  });
}

export function useDeleteRule() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ scorecardId, ruleId }: { scorecardId: string; ruleId: string }) =>
      deleteRule(scorecardId, ruleId),
    onSuccess: (_data, variables) => invalidateRules(client, variables.scorecardId),
  });
}

/** Прогон правила без сохранения. Ничего не меняет — кэш не трогаем. */
export function useTestRule() {
  return useMutation({
    mutationFn: (body: RuleTestRequest) => testRule(body),
  });
}

// ───────────────────── Сотрудники и добавочные ──────────────────────────

export function useCreateEmployee() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: EmployeeInput) => createEmployee(body),
    onSuccess: () => client.invalidateQueries({ queryKey: ['employees'] }),
  });
}

export function useUpdateEmployee() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: EmployeeInput }) => updateEmployee(id, body),
    onSuccess: () => client.invalidateQueries({ queryKey: ['employees'] }),
  });
}

/** Переводит в inactive, а не удаляет: на сотрудника ссылаются звонки. */
export function useDeactivateEmployee() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deactivateEmployee(id),
    onSuccess: () => client.invalidateQueries({ queryKey: ['employees'] }),
  });
}

/**
 * Закрепление добавочного.
 *
 * Подбирает уже принятые звонки в периоде действия привязки, поэтому после
 * него устарел не только список привязок, но и список звонков: у части из
 * них только что появился сотрудник.
 */
export function useCreateAssignment() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ employeeId, body }: { employeeId: string; body: AssignmentInput }) =>
      createAssignment(employeeId, body),
    onSuccess: (_data, variables) => {
      void client.invalidateQueries({ queryKey: queryKeys.assignments(variables.employeeId) });
      void client.invalidateQueries({ queryKey: ['employees'] });
      void client.invalidateQueries({ queryKey: ['calls'] });
    },
  });
}

/** Закрывает привязку датой. Звонки вне периода перестают быть его. */
export function useCloseAssignment() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({
      employeeId,
      assignmentId,
      activeTo,
    }: {
      employeeId: string;
      assignmentId: string;
      activeTo?: string;
    }) => closeAssignment(employeeId, assignmentId, activeTo),
    onSuccess: (_data, variables) => {
      void client.invalidateQueries({ queryKey: queryKeys.assignments(variables.employeeId) });
      void client.invalidateQueries({ queryKey: ['employees'] });
      void client.invalidateQueries({ queryKey: ['calls'] });
    },
  });
}

// ───────────────────── Пользователи и права доступа ─────────────────────

/**
 * Замена набора прав.
 *
 * После неё сбрасываем не только список пользователей: изменившиеся права
 * меняют область видимости, а значит и то, что человек увидит в звонках,
 * сотрудниках и дереве.
 */
export function useReplaceGrants() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, grants }: { userId: string; grants: GrantInput[] }) =>
      replaceUserGrants(userId, grants),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryKeys.users });
      void client.invalidateQueries({ queryKey: queryKeys.me });
    },
  });
}

/**
 * Основания раскрытия меняют, кто чьи данные видит, поэтому после них
 * устаревает вся выдача разом: звонки узла появляются или исчезают
 * следующим же запросом.
 */
function invalidateVisibility(client: ReturnType<typeof useQueryClient>) {
  void client.invalidateQueries({ queryKey: ['disclosure-grants'] });
  void client.invalidateQueries({ queryKey: ['calls'] });
  void client.invalidateQueries({ queryKey: ['employees'] });
  void client.invalidateQueries({ queryKey: queryKeys.units });
}

export function useCreateDisclosureGrant() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: DisclosureGrantInput) => createDisclosureGrant(body),
    onSuccess: () => invalidateVisibility(client),
  });
}

export function useRevokeDisclosureGrant() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => revokeDisclosureGrant(id),
    onSuccess: () => invalidateVisibility(client),
  });
}

export function useDeleteBinding() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ sourceId, bindingId }: { sourceId: string; bindingId: string }) =>
      deleteBinding(sourceId, bindingId),
    onSuccess: (_data, variables) => {
      void client.invalidateQueries({ queryKey: queryKeys.bindings(variables.sourceId) });
      void client.invalidateQueries({ queryKey: queryKeys.sources });
    },
  });
}

// ─────────────────────────── Платформенные АТС ──────────────────────────

export function useCreateServer() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: PbxServerInput) => createServer(body),
    onSuccess: () => client.invalidateQueries({ queryKey: queryKeys.servers }),
  });
}

export function useRotateServerSecret() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => rotateServerSecret(id),
    onSuccess: () => client.invalidateQueries({ queryKey: queryKeys.servers }),
  });
}

export function useCreateScorecard() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => createScorecard(name),
    onSuccess: () => client.invalidateQueries({ queryKey: queryKeys.scorecards }),
  });
}

export function useRenameScorecard() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) => renameScorecard(id, name),
    onSuccess: () => client.invalidateQueries({ queryKey: queryKeys.scorecards }),
  });
}

export function useDeleteScorecard() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteScorecard(id),
    onSuccess: () => client.invalidateQueries({ queryKey: queryKeys.scorecards }),
  });
}

// ─────────────────────── Справочники со станции ─────────────────────────

/**
 * Перенос записи справочника в другой узел меняет область видимости: звонки
 * этого добавочного начинают относиться к другому подразделению. Поэтому
 * сбрасываем не только сам справочник.
 */
function invalidateDirectory(client: ReturnType<typeof useQueryClient>, sourceId: string) {
  void client.invalidateQueries({ queryKey: ['sources', sourceId, 'directory'] });
  void client.invalidateQueries({ queryKey: ['calls'] });
}

export function useAssignExtensionUnit() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({
      sourceId,
      extensionId,
      unitId,
    }: {
      sourceId: string;
      extensionId: string;
      unitId: string;
    }) => assignExtensionUnit(sourceId, extensionId, unitId),
    onSuccess: (_data, variables) => invalidateDirectory(client, variables.sourceId),
  });
}

export function useAssignQueueUnit() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({
      sourceId,
      queueId,
      unitId,
    }: {
      sourceId: string;
      queueId: string;
      unitId: string;
    }) => assignQueueUnit(sourceId, queueId, unitId),
    onSuccess: (_data, variables) => invalidateDirectory(client, variables.sourceId),
  });
}

export function useUpdateDestinationRouting() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({
      sourceId,
      destinationId,
      body,
    }: {
      sourceId: string;
      destinationId: string;
      body: DestinationRoutingInput;
    }) => updateDestinationRouting(sourceId, destinationId, body),
    onSuccess: (_data, variables) => invalidateDirectory(client, variables.sourceId),
  });
}

export function useSetServerStereo() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, recordingStereo }: { id: string; recordingStereo: boolean }) =>
      setServerRecordingStereo(id, recordingStereo),
    onSuccess: () => client.invalidateQueries({ queryKey: queryKeys.servers }),
  });
}

// ─────────────────── Комментарии и разбор оценки ────────────────────────

export function useCreateComment() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ callId, text }: { callId: string; text: string }) => createComment(callId, text),
    onSuccess: (_data, variables) =>
      client.invalidateQueries({ queryKey: queryKeys.callComments(variables.callId) }),
  });
}

export function useDeleteComment() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ callId, commentId }: { callId: string; commentId: string }) =>
      deleteComment(callId, commentId),
    onSuccess: (_data, variables) =>
      client.invalidateQueries({ queryKey: queryKeys.callComments(variables.callId) }),
  });
}

/**
 * Правка результата правила.
 *
 * Ответ — уже пересчитанная карточка целиком, поэтому кладём её в кэш как
 * есть, а не перезапрашиваем: сервер посчитал балл, полосу и признак
 * критического нарушения, и повторное чтение только добавило бы задержку.
 * Список звонков при этом устарел — там свой балл и своя полоса.
 */
export function useReviewScoreResult() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({
      callId,
      resultId,
      result,
      comment,
    }: {
      callId: string;
      resultId: string;
      result: ReviewResult;
      comment?: string;
    }) => reviewScoreResult(callId, resultId, { result, comment }),
    onSuccess: (updated, variables) => {
      client.setQueryData(queryKeys.call(variables.callId), updated);
      void client.invalidateQueries({ queryKey: ['calls'] });
    },
  });
}

export function useCreateFromTemplate() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ template, name }: { template: string; name: string }) =>
      createScorecardFromTemplate(template, name),
    onSuccess: () => client.invalidateQueries({ queryKey: queryKeys.scorecards }),
  });
}

/**
 * Назначение чек-листа на направление.
 *
 * Сбрасываем и звонки: чек-лист определяет, по каким правилам их оценивают,
 * а наследование уносит назначение вниз по всему поддереву.
 */
export function useSetUnitScorecard() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({
      unitId,
      direction,
      scorecardId,
    }: {
      unitId: string;
      direction: ScorecardDirection;
      scorecardId: string | null;
    }) => setUnitScorecard(unitId, direction, scorecardId),
    onSuccess: (_data, variables) => {
      void client.invalidateQueries({ queryKey: ['units', variables.unitId, 'scorecard'] });
      void client.invalidateQueries({ queryKey: ['calls'] });
    },
  });
}

/** Звёзды. Ответ — пересчитанная карточка, кладём как есть. */
/**
 * Решение по разговору другого отдела.
 *
 * Ответ — пересчитанная карточка целиком, и она кладётся в кэш как есть:
 * основную оценку решение не меняет, но `status`, `decidedAt` и
 * `deciderName` приходят от сервера, а не досочиняются здесь.
 *
 * Список звонков тоже сбрасываем: у строки свой бейдж, и фильтр
 * `departmentSuggestion=pending` после решения должен отдать другой список —
 * иначе руководитель, проходящий очередь, решает по одному и тому же звонку
 * дважды.
 */
export function useDecideDepartmentSuggestion() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ callId, status }: { callId: string; status: DepartmentSuggestionStatus }) =>
      decideDepartmentSuggestion(callId, status),
    onSuccess: (updated, variables) => {
      client.setQueryData(queryKeys.call(variables.callId), updated);
      void client.invalidateQueries({ queryKey: ['calls'] });
    },
    /* 404 значит «предложения у звонка больше нет»: повторный разбор мог
       снять его, пока карточка была открыта. Перечитываем карточку сами —
       иначе блок с кнопками остался бы на экране и человек нажимал бы в
       пустоту. */
    onError: (error, variables) => {
      if (error instanceof ApiError && error.isNotFound) {
        void client.invalidateQueries({ queryKey: queryKeys.call(variables.callId) });
      }
    },
  });
}

export function useSetCallRating() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({
      callId,
      rating,
      comment,
    }: {
      callId: string;
      rating: number | null;
      comment?: string;
    }) => setCallRating(callId, rating, comment),
    onSuccess: (updated, variables) => {
      client.setQueryData(queryKeys.call(variables.callId), updated);
      // В списке звонков своя колонка со звёздами.
      void client.invalidateQueries({ queryKey: ['calls'] });
    },
  });
}

// ──────────────────────────────── Теги ─────────────────────────────────

function invalidateTags(client: ReturnType<typeof useQueryClient>) {
  void client.invalidateQueries({ queryKey: ['tags'] });
  // В строках списка звонков теги приезжают вместе со звонком.
  void client.invalidateQueries({ queryKey: ['calls'] });
}

export function useCreateTag() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: TagInput) => createTag(body),
    onSuccess: () => invalidateTags(client),
  });
}

export function useUpdateTag() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: TagInput }) => updateTag(id, body),
    onSuccess: () => invalidateTags(client),
  });
}

export function useDeleteTag() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteTag(id),
    onSuccess: () => invalidateTags(client),
  });
}

export function useAddCallTag() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ callId, tagId }: { callId: string; tagId: string }) => addCallTag(callId, tagId),
    onSuccess: (_tags, variables) => {
      void client.invalidateQueries({ queryKey: queryKeys.call(variables.callId) });
      void client.invalidateQueries({ queryKey: ['calls'] });
      // callCount у тега изменился.
      void client.invalidateQueries({ queryKey: ['tags'] });
    },
  });
}

export function useRemoveCallTag() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ callId, tagId }: { callId: string; tagId: string }) =>
      removeCallTag(callId, tagId),
    onSuccess: (_tags, variables) => {
      void client.invalidateQueries({ queryKey: queryKeys.call(variables.callId) });
      void client.invalidateQueries({ queryKey: ['calls'] });
      void client.invalidateQueries({ queryKey: ['tags'] });
    },
  });
}

export function useCreateTagRule() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: TagRuleInput) => createTagRule(body),
    onSuccess: () => client.invalidateQueries({ queryKey: ['tag-rules'] }),
  });
}

export function useUpdateTagRule() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: TagRuleInput }) => updateTagRule(id, body),
    onSuccess: () => client.invalidateQueries({ queryKey: ['tag-rules'] }),
  });
}

export function useDeleteTagRule() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteTagRule(id),
    onSuccess: () => client.invalidateQueries({ queryKey: ['tag-rules'] }),
  });
}

export function useTestTagRule() {
  return useMutation({
    mutationFn: ({ callId, conditions }: { callId: string; conditions: TagRuleConditions }) =>
      testTagRule(callId, conditions),
  });
}

export function useUpdateContact() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: ContactInput }) => updateContact(id, body),
    onSuccess: (_data, variables) => {
      void client.invalidateQueries({ queryKey: ['contacts', variables.id] });
      // Имя клиента показывается в строке журнала.
      void client.invalidateQueries({ queryKey: ['calls'] });
    },
  });
}

// ──────────────────────────── Оповещения ───────────────────────────────

/**
 * Правит кэш оповещений на месте, не дожидаясь ответа.
 *
 * Счётчик на иконке иначе моргает: отметка прочитанной уходит мгновенно, а
 * число меняется только после перечитывания — то есть через сеть. `readAt`
 * ставим свой, но **`unreadCount` уменьшаем, а не пересчитываем по `items`**:
 * список подрезан лимитом, и пересчёт по нему соврал бы, как только
 * непрочитанного станет больше страницы. Настоящее число придёт следующим
 * ответом сервера и перекроет наше.
 */
function patchNotificationCache(
  client: ReturnType<typeof useQueryClient>,
  change: (page: NotificationsPage) => NotificationsPage
) {
  // Ключи оповещений различаются фильтром непрочитанного — правим все.
  client.setQueriesData<NotificationsPage>({ queryKey: ['notifications'] }, (page) =>
    page ? change(page) : page
  );
}

export function useMarkNotificationRead() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => markNotificationRead(id),
    onMutate: (id) => {
      // Снимок на случай отказа: вернуть всё как было проще, чем угадывать.
      const previous = client.getQueriesData<NotificationsPage>({ queryKey: ['notifications'] });
      const readAt = new Date().toISOString();

      patchNotificationCache(client, (page) => {
        const target = page.items.find((item) => item.id === id);
        // Уже прочитанное повторным нажатием счётчик не трогает.
        if (!target || target.readAt) return page;
        return {
          unreadCount: Math.max(0, page.unreadCount - 1),
          items: page.items.map((item) => (item.id === id ? { ...item, readAt } : item)),
        };
      });

      return { previous };
    },
    onError: (_error, _id, context) => {
      for (const [key, value] of context?.previous ?? []) client.setQueryData(key, value);
    },
    onSuccess: () => client.invalidateQueries({ queryKey: ['notifications'] }),
  });
}

export function useMarkAllRead() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => markAllNotificationsRead(),
    onMutate: () => {
      const previous = client.getQueriesData<NotificationsPage>({ queryKey: ['notifications'] });
      const readAt = new Date().toISOString();

      /* Прочитанные из списка не выкидываем: человек возвращается к «что там
         было вчера», и список, который очищается, для этого бесполезен. */
      patchNotificationCache(client, (page) => ({
        unreadCount: 0,
        items: page.items.map((item) => (item.readAt ? item : { ...item, readAt })),
      }));

      return { previous };
    },
    onError: (_error, _variables, context) => {
      for (const [key, value] of context?.previous ?? []) client.setQueryData(key, value);
    },
    onSuccess: () => client.invalidateQueries({ queryKey: ['notifications'] }),
  });
}

export function useCreateNotificationRule() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: NotificationRuleInput) => createNotificationRule(body),
    onSuccess: () => client.invalidateQueries({ queryKey: ['notification-rules'] }),
  });
}

export function useUpdateNotificationRule() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: NotificationRuleInput }) =>
      updateNotificationRule(id, body),
    onSuccess: () => client.invalidateQueries({ queryKey: ['notification-rules'] }),
  });
}

export function useDeleteNotificationRule() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteNotificationRule(id),
    onSuccess: () => client.invalidateQueries({ queryKey: ['notification-rules'] }),
  });
}

/**
 * Проверка черновика — того, что сейчас в форме.
 *
 * Отдельная от проверки сохранённого правила намеренно: «адрес отвечает» при
 * незаполненном телефоне даёт зелёную галочку правилу, которое в бою
 * доставит сообщение в никуда.
 */
export function useTestWebhookDraft() {
  return useMutation({ mutationFn: (body: WebhookDraftTest) => testWebhookDraft(body) });
}

/** Проверка сохранённого правила его настоящими адресом, получателем и шаблоном. */
export function useTestNotificationRule() {
  return useMutation({ mutationFn: (id: string) => testNotificationRule(id) });
}

// ─────────────────── Отметки об отработке пропущенных ───────────────────

/**
 * После отметки перечитываем и список, и сам звонок: отметка меняет `state`
 * строки, а «потеряно» на дашборде считается из тех же данных — оставить их
 * расходиться нельзя.
 */
export function useCreateMissedAction() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ callId, body }: { callId: string; body: MissedActionInput }) =>
      createMissedAction(callId, body),
    onSuccess: (_result, { callId }) => {
      void client.invalidateQueries({ queryKey: ['missed-calls'] });
      void client.invalidateQueries({ queryKey: ['missed-actions', callId] });
      void client.invalidateQueries({ queryKey: ['analytics', 'dashboard'] });
    },
  });
}

export function useDeleteMissedAction() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ callId, actionId }: { callId: string; actionId: number }) =>
      deleteMissedAction(callId, actionId),
    onSuccess: (_result, { callId }) => {
      void client.invalidateQueries({ queryKey: ['missed-calls'] });
      void client.invalidateQueries({ queryKey: ['missed-actions', callId] });
      void client.invalidateQueries({ queryKey: ['analytics', 'dashboard'] });
    },
  });
}

// ─────────────────── Дополнительные поля разбора ────────────────────────

export function useCreateCallField() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: CallFieldInput) => createCallField(body),
    onSuccess: () => client.invalidateQueries({ queryKey: ['call-fields'] }),
  });
}

export function useUpdateCallField() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: CallFieldInput }) => updateCallField(id, body),
    onSuccess: () => client.invalidateQueries({ queryKey: ['call-fields'] }),
  });
}

export function useDeleteCallField() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteCallField(id),
    onSuccess: () => client.invalidateQueries({ queryKey: ['call-fields'] }),
  });
}

export function useApplyCallFieldTemplate() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (template: string) => applyCallFieldTemplate(template),
    onSuccess: () => client.invalidateQueries({ queryKey: ['call-fields'] }),
  });
}

/* ------------------------------------------------------------------ */
/* Глобальный администратор                                            */
/* ------------------------------------------------------------------ */

/** Подключённые организации уходят из списка стучавшихся сами — но кэш об этом не знает. */
export function useCreateAdminTenant() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: AdminTenantInput) => createAdminTenant(body),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['admin'] });
    },
  });
}

export function useHidePendingOrganization() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (orgId: string) => hidePendingOrganization(orgId),
    onSuccess: () => client.invalidateQueries({ queryKey: ['admin', 'pending-organizations'] }),
  });
}

/**
 * Назначение меняет и список администраторов, и счётчик пользователей в
 * строке клиента — перечитываем обе ветки.
 */
export function useAddTenantAdmin() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ tenantId, email }: { tenantId: string; email: string }) =>
      addTenantAdmin(tenantId, email),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['admin'] });
    },
  });
}

export function useRemoveTenantAdmin() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ tenantId, adminId }: { tenantId: string; adminId: string }) =>
      removeTenantAdmin(tenantId, adminId),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['admin'] });
    },
  });
}

/** Набор заводит и теги, и правила — перечитываем обе ветки. */
export function useApplyTagTemplate() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ template, rules }: { template: string; rules: boolean }) =>
      applyTagTemplate(template, rules),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['tags'] });
      void client.invalidateQueries({ queryKey: ['tag-rules'] });
    },
  });
}

/* ------------------------------------------------------------------ */
/* Хранилища записей и сроки хранения                                   */
/* ------------------------------------------------------------------ */

export function useCreateStorageProfile() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: StorageProfileInput) => createStorageProfile(body),
    onSuccess: () => client.invalidateQueries({ queryKey: ['storage-profiles'] }),
  });
}

export function useUpdateStorageProfile() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: StorageProfileInput }) =>
      updateStorageProfile(id, body),
    onSuccess: () => client.invalidateQueries({ queryKey: ['storage-profiles'] }),
  });
}

export function useDeleteStorageProfile() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteStorageProfile(id),
    onSuccess: () => client.invalidateQueries({ queryKey: ['storage-profiles'] }),
  });
}

/** Неудача приходит двухсоткой — ошибкой её считать нельзя. */
export function useTestStorageProfile() {
  return useMutation({ mutationFn: (id: string) => testStorageProfile(id) });
}

/** Счётчики `servers`/`sources` в профиле меняются вместе с привязкой. */
export function useSetServerStorage() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({
      serverId,
      storageProfileId,
    }: {
      serverId: string;
      storageProfileId: string | null;
    }) => setServerStorage(serverId, storageProfileId),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['servers'] });
      void client.invalidateQueries({ queryKey: ['storage-profiles'] });
    },
  });
}

export function useSetSourceStorage() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({
      sourceId,
      storageProfileId,
    }: {
      sourceId: string;
      storageProfileId: string | null;
    }) => setSourceStorage(sourceId, storageProfileId),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['sources'] });
      void client.invalidateQueries({ queryKey: ['storage-profiles'] });
    },
  });
}

export function useSetTenantRetention() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ tenantId, retentionDays }: { tenantId: string; retentionDays: number | null }) =>
      setTenantRetention(tenantId, retentionDays),
    onSuccess: () => client.invalidateQueries({ queryKey: ['admin'] }),
  });
}

/* ------------------------------------------------------------------ */
/* Мои оповещения                                                       */
/* ------------------------------------------------------------------ */

/**
 * Обе правки возвращают настройки целиком, поэтому кладём ответ в кэш, а не
 * перечитываем: лишний запрос показал бы старое состояние на один кадр.
 *
 * Счётчики подписчиков в списке правил меняются вместе с подпиской — их
 * инвалидируем.
 */
export function useSetNotificationContacts() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: NotificationContactsInput) => setNotificationContacts(body),
    onSuccess: (settings) => client.setQueryData(['notification-settings'], settings),
  });
}

export function useSetNotificationSubscription() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({
      ruleId,
      inApp,
      webhook,
    }: {
      ruleId: string;
      inApp: boolean;
      webhook: boolean;
    }) => setNotificationSubscription(ruleId, { inApp, webhook }),
    onSuccess: (settings) => {
      client.setQueryData(['notification-settings'], settings);
      void client.invalidateQueries({ queryKey: ['notification-rules'] });
    },
  });
}

/* ------------------------------------------------------------------ */
/* Настройка разбора                                                    */
/* ------------------------------------------------------------------ */

/** Ответ кладём в кэш: он приходит целиком, и перечитывать нечего. */
export function useUpdateAnalysisSettings() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: AnalysisSettingsInput) => updateAnalysisSettings(body),
    onSuccess: (settings) => client.setQueryData(['analysis-settings'], settings),
  });
}

export function useCreateVocabularyItem() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: VocabularyInput) => createVocabularyItem(body),
    onSuccess: () => client.invalidateQueries({ queryKey: ['vocabulary'] }),
  });
}

export function useUpdateVocabularyItem() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: VocabularyInput }) =>
      updateVocabularyItem(id, body),
    onSuccess: () => client.invalidateQueries({ queryKey: ['vocabulary'] }),
  });
}

export function useDeleteVocabularyItem() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteVocabularyItem(id),
    onSuccess: () => client.invalidateQueries({ queryKey: ['vocabulary'] }),
  });
}

/* ------------------------------------------------------------------ */
/* Исходящие интеграции                                                 */
/* ------------------------------------------------------------------ */

/** Сводка по доставкам живёт в самом списке — его и перечитываем. */
function invalidateIntegrations(client: ReturnType<typeof useQueryClient>) {
  void client.invalidateQueries({ queryKey: queryKeys.integrations });
}

export function useCreateIntegration() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: IntegrationInput) => createIntegration(body),
    onSuccess: () => invalidateIntegrations(client),
  });
}

export function useUpdateIntegration() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: IntegrationInput }) =>
      updateIntegration(id, body),
    onSuccess: (updated) => {
      client.setQueryData(queryKeys.integration(updated.id), updated);
      invalidateIntegrations(client);
    },
  });
}

export function useDeleteIntegration() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteIntegration(id),
    onSuccess: () => invalidateIntegrations(client),
  });
}

/**
 * Замена секретов.
 *
 * Тело собирает вызывающий: поле, которого человек не трогал, отправлять
 * нельзя — отсутствие оставляет прежнее значение, а пустая строка стирает.
 */
export function useSetIntegrationSecrets() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: IntegrationSecretsInput }) =>
      setIntegrationSecrets(id, body),
    onSuccess: (updated) => {
      client.setQueryData(queryKeys.integration(updated.id), updated);
      invalidateIntegrations(client);
    },
  });
}

/**
 * Проверка связи. Ничего не инвалидирует: она ничего не меняет, а `ok: false`
 * — это ответ, который показывают на экране, а не ошибка запроса.
 */
export function useTestIntegration() {
  return useMutation({
    mutationFn: (id: string) => testIntegration(id),
  });
}

/**
 * Правка соответствий. Уезжают только изменённые строки — набор целиком не
 * отправляется намеренно: две открытые формы иначе затирали бы друг друга.
 */
export function usePutIntegrationMappings() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: IntegrationMappingsInput }) =>
      putIntegrationMappings(id, body),
    onSuccess: (_result, variables) =>
      client.invalidateQueries({ queryKey: queryKeys.integrationMappings(variables.id) }),
  });
}

/**
 * Проверка справочника контактов.
 *
 * Как и проверка связи, ничего не инвалидирует — и не потому, что лень:
 * ручка ничего не пишет, очередь обогащения от неё не двигается.
 */
export function useTestIntegrationLookup() {
  return useMutation({
    mutationFn: ({ id, phone }: { id: string; phone: string }) => testIntegrationLookup(id, phone),
  });
}

/* ------------------------------------------------------------------ */
/* Коллтрекинг                                                          */
/* ------------------------------------------------------------------ */

/**
 * Правок здесь мало, а связей между ними много: зачисление меняет и номера, и
 * сводку пула, и список доступных со станции. Поэтому сбрасываем всю ветку —
 * разъехавшийся ключ не ломает сборку, он просто оставляет на экране
 * устаревшее число свободных номеров, и заметно это только глазами.
 */
function invalidateCalltracking(client: ReturnType<typeof useQueryClient>) {
  void client.invalidateQueries({ queryKey: ['calltracking'] });
}

export function useCreatePool() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: CtPoolInput) => createPool(body),
    onSuccess: () => invalidateCalltracking(client),
  });
}

export function useUpdatePool() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: CtPoolInput }) => updatePool(id, body),
    onSuccess: () => invalidateCalltracking(client),
  });
}

/** Маршрут завели мы: номер связывается с назначением, узел дальше со станции. */
export function useAcceptStationRoute() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => acceptStationRoute(id),
    onSuccess: () => invalidateCalltracking(client),
  });
}

export function useUpdateCtNumber() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: CtNumberPatch }) => updateCtNumber(id, body),
    onSuccess: () => invalidateCalltracking(client),
  });
}

/**
 * Зачисление номеров.
 *
 * Холостой прогон ходит той же мутацией: ответ у него тот же, а разница только
 * в `dryRun`. Кэш сбрасываем лишь при настоящем зачислении — прогон ничего не
 * менял.
 */
export function useEnrollNumbers() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: CtEnrollRequest) => enrollNumbers(body),
    onSuccess: (_result, variables) => {
      if (!variables.dryRun) invalidateCalltracking(client);
    },
  });
}

export function useCreateCtSite() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: CtSiteInput) => createCtSite(body),
    onSuccess: () => invalidateCalltracking(client),
  });
}

export function useUpdateCtSite() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: CtSiteInput }) => updateCtSite(id, body),
    onSuccess: () => invalidateCalltracking(client),
  });
}

export function useCreateStaticRule() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: CtStaticRuleInput) => createStaticRule(body),
    onSuccess: () => invalidateCalltracking(client),
  });
}

export function useUpdateStaticRule() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: CtStaticRuleInput }) =>
      updateStaticRule(id, body),
    onSuccess: () => invalidateCalltracking(client),
  });
}

export function useDeleteStaticRule() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteStaticRule(id),
    onSuccess: () => invalidateCalltracking(client),
  });
}

/* ------------------------------------------------------------------ */
/* Цели звонка                                                          */
/* ------------------------------------------------------------------ */

function invalidateGoals(client: ReturnType<typeof useQueryClient>) {
  void client.invalidateQueries({ queryKey: ['call-goals'] });
  /* Цель требует полей, а поле «по требованию» без включённой цели не
     спрашивается нигде: список полей зависит от целей и наоборот. */
  void client.invalidateQueries({ queryKey: ['call-fields'] });
}

export function useCreateCallGoal() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: CallGoalInput) => createCallGoal(body),
    onSuccess: () => invalidateGoals(client),
  });
}

export function useUpdateCallGoal() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: CallGoalInput }) => updateCallGoal(id, body),
    onSuccess: () => invalidateGoals(client),
  });
}

export function useDeleteCallGoal() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteCallGoal(id),
    onSuccess: () => invalidateGoals(client),
  });
}

/** Примерка ничего не пишет — и кэш ей сбрасывать нечего. */
export function useDryRunCallGoal() {
  return useMutation({ mutationFn: (body: CallGoalDryRunInput) => dryRunCallGoal(body) });
}

/* ------------------------------------------------------------------ */
/* Названия кампаний и дополнения промтов                               */
/* ------------------------------------------------------------------ */

export function usePutAdNames() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (items: AdNameInput[]) => putAdNames(items),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['calltracking', 'ad-names'] });
      // Названия подставляются в отчёт по источникам — он тоже устарел.
      void client.invalidateQueries({ queryKey: ['analytics', 'calltracking'] });
    },
  });
}

export function useDeleteAdName() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({
      source,
      kind,
      externalId,
    }: {
      source: string;
      kind: string;
      externalId: string;
    }) => deleteAdName(source, kind, externalId),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['calltracking', 'ad-names'] });
      void client.invalidateQueries({ queryKey: ['analytics', 'calltracking'] });
    },
  });
}

export function useSetUnitAddendum() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ unitId, body }: { unitId: string; body: UnitPromptAddendumInput }) =>
      setUnitAddendum(unitId, body),
    onSuccess: () => client.invalidateQueries({ queryKey: ['analysis-settings'] }),
  });
}

export function useDeleteUnitAddendum() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (unitId: string) => deleteUnitAddendum(unitId),
    onSuccess: () => client.invalidateQueries({ queryKey: ['analysis-settings'] }),
  });
}
